import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import useChannel from "hooks/use-channel";
import { createRequest } from "hooks/use-fetch";
import { postJson, putJson } from "utils/fetch";
import { transformErrors } from "utils/transform-errors";

import ValidationErrors from "screens/shared/validation-errors";

import {
  Button,
  Container,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  Stack,
  Typography,
} from "components/core";
import NavigationBar from "components/navigation-bar";

const CheckPostcode = ({ channelKey, mobileNumber, restartFlow }) => {
  const [code, setCode] = useState("");
  const [postcode, setPostcode] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const codeRef = useRef(null);

  const check = useMemo(
    () => createRequest(putJson, setLoading, "leading"),
    [],
  );

  const login = useMemo(
    () => createRequest(postJson, setLoading, "leading"),
    [],
  );

  useEffect(() => {
    if (!channelKey || !mobileNumber) {
      restartFlow([], history);
    }
  }, [channelKey, mobileNumber, restartFlow, history]);

  const handleBackClick = () => {
    history.push("/");
  };

  const onReceived = useCallback(
    async (data) => {
      if (data.token) {
        const response = await login("/account/api/phone_logins/authenticate", {
          token: data.token,
          code: codeRef.current.value,
        });

        window.location = response.nextPath;
      } else if (data.retryable) {
        setLoading(false);
        setErrors([data.error]);
      } else {
        setLoading(false);
        restartFlow([data.error], history);
      }
    },
    [login, history, restartFlow],
  );

  const onDisconnected = useCallback(() => {
    setLoading(false);
  }, []);

  const [connected] = useChannel({
    channel: "Account::VerifyChannel",
    key: channelKey,
    onReceived,
    onDisconnected,
  });

  const handleCodeChange = (e) => setCode(e.target.value);
  const handlePostcodeChange = (e) => setPostcode(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const response = await check("/account/api/phone_logins", {
      channelKey,
      mobileNumber,
      postcode,
      code,
    });

    if (response.errors) {
      setErrors(transformErrors(response.errors));
      setLoading(false);
    }
  };

  const textTransform = postcode && "uppercase";

  return (
    <Container disableGutters maxWidth="xs">
      <NavigationBar onBackClick={handleBackClick} />
      <Stack
        component="form"
        pb={[2, 3]}
        pt={[3, 4]}
        px={[2, 3]}
        spacing={5}
        onSubmit={handleSubmit}
      >
        <ValidationErrors dangerouslyAllowHtml errors={errors} />
        <Typography lineHeight="1.5" variant="title">
          Log in
        </Typography>
        <FormControl error={!!errors.code}>
          <FormLabel htmlFor="code-input" sx={{ mb: 1 }} variant="standalone">
            Enter the code we’ve sent to {mobileNumber}
          </FormLabel>
          <OutlinedInput
            autoFocus
            autoComplete="one-time-code"
            id="code-input"
            inputProps={{
              "data-1p-ignore": true,
              inputMode: "numeric",
            }}
            inputRef={codeRef}
            pattern="[0–9]*"
            placeholder="––––––"
            readOnly={loading}
            sx={{
              input: {
                letterSpacing: 15,
                fontSize: 18,
                textAlign: "center",
              },
            }}
            value={code}
            onChange={handleCodeChange}
          />
          {errors.code && (
            <FormHelperText variant="standard">{errors.code}</FormHelperText>
          )}
        </FormControl>
        <FormControl error={!!errors.postcode}>
          <FormLabel
            htmlFor="postcode-input"
            sx={{ mb: 1 }}
            variant="standalone"
          >
            Enter your postcode so we can make sure it’s your account
          </FormLabel>
          <OutlinedInput
            autoComplete="postal-code"
            id="postcode-input"
            placeholder="e.g. SW1A 2AA"
            sx={{
              input: {
                fontSize: 18,
                textAlign: "center",
                textTransform,
              },
            }}
            value={postcode}
            onChange={handlePostcodeChange}
          />
          <FormHelperText variant="standard">
            {errors.postcode || (
              <span>
                This is the only time you’ll need to verify your postcode.
              </span>
            )}
          </FormHelperText>
        </FormControl>
        <Button loading={!connected || loading} type="submit">
          Log in
        </Button>
      </Stack>
    </Container>
  );
};

CheckPostcode.propTypes = {
  mobileNumber: PropTypes.string.isRequired,
  restartFlow: PropTypes.func.isRequired,
  channelKey: PropTypes.string,
};

export default CheckPostcode;
